import { useRouter, useSearch } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, FileUp, Plus, Trash2, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { CurrencyInput } from "@/components/buy-log/CurrencyInput";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import type { BudgetLineItem, CostCode } from "@/hooks/use-budgets";
import { useProjectBudget, useProjectCostCodes } from "@/hooks/use-budgets";
import type { CreateCommitmentPayload } from "@/hooks/use-commitments";
import { useCreateCommitment, useUploadContractDocument } from "@/hooks/use-commitments";
import { useCompanies } from "@/hooks/use-companies";
import { formatCurrency, toNumber } from "@/lib/formatters";

export interface DraftG703Line {
  localId: string;
  itemNo: string;
  description: string;
  scheduledValue: string;
  costCodeId: string;
}

type InitialStatus = "draft" | "pending_execution" | "executed";

const STATUS_LABELS: Record<InitialStatus, string> = {
  draft: "Draft",
  pending_execution: "Pending Execution",
  executed: "Executed",
};

export function freshLine(itemNo: number, defaultCostCodeId: string): DraftG703Line {
  return {
    localId: crypto.randomUUID(),
    itemNo: String(itemNo).padStart(3, "0"),
    description: "",
    scheduledValue: "",
    costCodeId: defaultCostCodeId,
  };
}

// ── Shared sub-components ──

export function FileUploadField({
  file,
  onSelect,
  onClear,
}: {
  file: File | null;
  onSelect: (f: File) => void;
  onClear: () => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  return (
    <div className="space-y-1.5">
      <Label htmlFor="c-file">Contract Document (optional)</Label>
      {file ? (
        <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
          <div className="flex items-center gap-2">
            <FileUp className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-800">{file.name}</span>
            <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(0)} KB)</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onClear();
              if (ref.current) ref.current.value = "";
            }}
            className="h-7 w-7 p-0"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <Input
          id="c-file"
          ref={ref}
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onSelect(f);
          }}
        />
      )}
      <p className="text-xs text-gray-500">PDF, Word, or text. Max 25 MB.</p>
    </div>
  );
}

export function G703Table({
  lines,
  costCodes,
  defaultCostCodeId: _defaultCostCodeId,
  totalAward,
  onUpdate,
  onAdd,
  onRemove,
}: {
  lines: DraftG703Line[];
  costCodes: CostCode[];
  defaultCostCodeId: string;
  totalAward: number | null;
  onUpdate: (localId: string, patch: Partial<DraftG703Line>) => void;
  onAdd: () => void;
  onRemove: (localId: string) => void;
}) {
  const linesTotal = lines.reduce((sum, l) => sum + toNumber(l.scheduledValue), 0);
  const totalsMatch = totalAward === null || Math.abs(linesTotal - totalAward) < 0.01;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Schedule of Values (G703)</Label>
        <Button type="button" variant="outline" size="sm" onClick={() => onAdd()}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add Line
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Item #</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Description</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Cost Code</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                Scheduled Value
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.localId} className="border-t">
                <td className="px-2 py-1.5">
                  <Input
                    className="h-8 w-20"
                    value={l.itemNo}
                    onChange={(e) => onUpdate(l.localId, { itemNo: e.target.value })}
                    required
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Input
                    className="h-8"
                    value={l.description}
                    onChange={(e) => onUpdate(l.localId, { description: e.target.value })}
                    required
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Select
                    value={l.costCodeId}
                    onValueChange={(v) => onUpdate(l.localId, { costCodeId: v })}
                  >
                    <SelectTrigger className="h-8 w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {costCodes.map((code) => (
                        <SelectItem key={code.id} value={code.id}>
                          {code.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-1.5">
                  <CurrencyInput
                    value={l.scheduledValue}
                    onCommit={(next) => onUpdate(l.localId, { scheduledValue: next })}
                    ariaLabel={`Line ${l.itemNo} scheduled value`}
                    className="w-32"
                  />
                </td>
                <td className="px-2 py-1.5 text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(l.localId)}
                    disabled={lines.length === 1}
                    className="h-8 w-8 p-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
            <tr className="border-t bg-gray-50">
              <td colSpan={3} className="px-3 py-2 text-right text-xs font-semibold text-gray-600">
                Schedule Total
              </td>
              <td className="px-3 py-2 text-right text-sm font-semibold text-gray-900">
                {formatCurrency(linesTotal, { precise: true })}
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      {!totalsMatch && totalAward !== null ? (
        <p className="text-xs text-amber-600">
          Schedule of Values total ({formatCurrency(linesTotal, { precise: true })}) does not match
          the Buy Log award ({formatCurrency(totalAward, { precise: true })}).
        </p>
      ) : null}
    </div>
  );
}

// ── Shared footer ──

function FormFooter({
  isPending,
  uploadPending,
  createPending,
  disabled,
  onCancel,
}: {
  isPending: boolean;
  uploadPending: boolean;
  createPending: boolean;
  disabled: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 bg-white px-6 py-3">
      <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
        Cancel
      </Button>
      <Button type="submit" disabled={disabled || isPending}>
        {uploadPending ? "Uploading..." : createPending ? "Creating..." : "Create Commitment"}
      </Button>
    </div>
  );
}

// ── Buy Log form (pre-filled from a budget line item) ──

function BuyLogCommitmentForm({
  lineItem,
  projectId,
  costCodes,
}: {
  lineItem: BudgetLineItem;
  projectId: string;
  costCodes: CostCode[];
}) {
  const router = useRouter();
  const createMutation = useCreateCommitment();
  const uploadMutation = useUploadContractDocument();
  const companies = useCompanies();

  const totalAward =
    toNumber(lineItem.awardValue) + toNumber(lineItem.allowance) + toNumber(lineItem.holdsAtBuy);

  const [commitmentType, setCommitmentType] = useState<"subcontract" | "purchase_order">(
    "subcontract",
  );
  const [status, setStatus] = useState<InitialStatus>("draft");
  const [title, setTitle] = useState(lineItem.description);
  const [companyId, setCompanyId] = useState<string>(lineItem.awardedCompanyId ?? "");
  const [startDate, setStartDate] = useState("");
  const [scheduledEndDate, setScheduledEndDate] = useState("");
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [lines, setLines] = useState<DraftG703Line[]>([
    {
      localId: crypto.randomUUID(),
      itemNo: "001",
      description: lineItem.description,
      scheduledValue: String(totalAward.toFixed(2)),
      costCodeId: lineItem.costCodeId,
    },
  ]);

  const linesValid =
    lines.length > 0 && lines.every((l) => l.description.trim() && l.itemNo.trim());
  const linesTotal = lines.reduce((sum, l) => sum + toNumber(l.scheduledValue), 0);
  const totalsMatch = Math.abs(linesTotal - totalAward) < 0.01;
  const isPending = createMutation.isPending || uploadMutation.isPending;

  const subcontractorCompanies = (companies.data ?? []).filter((c) => {
    if (!c.type) return true;
    const allowed = new Set([
      "subcontractor",
      "vendor",
      "contractor",
      "general_contractor",
      "material_supplier",
    ]);
    return allowed.has(c.type);
  });

  const selectedCompany = subcontractorCompanies.find((c) => c.id === companyId);
  const awardedCompanyMissing = !lineItem.awardedCompanyId;

  function updateLine(localId: string, patch: Partial<DraftG703Line>) {
    setLines((prev) => prev.map((l) => (l.localId === localId ? { ...l, ...patch } : l)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!linesValid) {
      toast.error("Each Schedule of Values line needs an item number and description.");
      return;
    }
    if (!companyId) {
      toast.error("Pick a subcontractor before creating the commitment.");
      return;
    }

    const payload: CreateCommitmentPayload = {
      projectId,
      companyId,
      toCompanyId: companyId,
      commitmentType,
      direction: "outbound",
      status,
      title: title.trim() || lineItem.description,
      originalAmount: String(totalAward.toFixed(2)),
      startDate: startDate || undefined,
      scheduledEndDate: scheduledEndDate || undefined,
      primaryBudgetLineId: lineItem.id,
      lineItems: lines.map((l) => ({
        itemNo: l.itemNo,
        description: l.description,
        scheduledValue: l.scheduledValue === "" ? "0" : l.scheduledValue,
        costCodeId: l.costCodeId || null,
        isAllowance: lineItem.isAllowance,
      })),
    };

    createMutation.mutate(payload, {
      onSuccess: async (res) => {
        const newId = res.data.id;
        if (contractFile && newId) {
          try {
            await uploadMutation.mutateAsync({ id: newId, file: contractFile });
            toast.success("Contract document uploaded");
          } catch {
            return;
          }
        }
        // Tell the Buy Log which row to highlight when the user lands back
        // on it. Picked up by BuyLogTab on mount, then cleared.
        sessionStorage.setItem("buyLogRecentlyCommittedLineId", lineItem.id);
        router.history.back();
      },
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto space-y-5 px-6 py-5">
        {/* Source line-item summary */}
        <div className="space-y-1 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
            Source Buy Log Line
          </div>
          <div className="text-sm font-medium text-gray-900">
            {lineItem.costCode?.code ?? "--"} — {lineItem.description}
          </div>
          <div className="text-xs text-gray-500">
            Subcontractor:{" "}
            <span className="font-medium text-gray-800">
              {lineItem.awardedCompany?.companyName ?? "Unassigned"}
            </span>{" "}
            · Award: <span className="font-medium text-gray-800">{formatCurrency(totalAward)}</span>
          </div>
        </div>

        {awardedCompanyMissing ? (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <div className="font-medium">This Buy Log line has no awarded subcontractor.</div>
              <div className="mt-0.5 text-xs">
                Pick one below to continue, or set it on the Buy Log first so it carries over to
                future commitments.
              </div>
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="c-type">Commitment Type</Label>
            <Select
              value={commitmentType}
              onValueChange={(v) => setCommitmentType(v as "subcontract" | "purchase_order")}
            >
              <SelectTrigger id="c-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="subcontract">Subcontract</SelectItem>
                <SelectItem value="purchase_order">Purchase Order</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-status">Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as InitialStatus)}>
              <SelectTrigger id="c-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(STATUS_LABELS) as InitialStatus[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1.5 md:col-span-1">
            <Label htmlFor="c-title">Title</Label>
            <Input
              id="c-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Concrete subcontract"
            />
          </div>
          <div className="col-span-2 space-y-1.5 md:col-span-3">
            <Label htmlFor="c-company">Subcontractor *</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger id="c-company">
                <SelectValue placeholder="Select a subcontractor">
                  {selectedCompany?.companyName ?? undefined}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {subcontractorCompanies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.companyName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Start Date</Label>
            <DatePicker
              value={startDate || null}
              onChange={(next) => setStartDate(next ?? "")}
              ariaLabel="Start date"
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Scheduled End Date</Label>
            <DatePicker
              value={scheduledEndDate || null}
              onChange={(next) => setScheduledEndDate(next ?? "")}
              ariaLabel="Scheduled end date"
              className="w-full"
            />
          </div>
        </div>

        <FileUploadField
          file={contractFile}
          onSelect={setContractFile}
          onClear={() => setContractFile(null)}
        />

        <G703Table
          lines={lines}
          costCodes={costCodes}
          defaultCostCodeId={lineItem.costCodeId}
          totalAward={totalAward}
          onUpdate={updateLine}
          onAdd={() =>
            setLines((prev) => [...prev, freshLine(prev.length + 1, lineItem.costCodeId)])
          }
          onRemove={(id) => setLines((prev) => prev.filter((l) => l.localId !== id))}
        />
      </div>

      <FormFooter
        isPending={isPending}
        uploadPending={uploadMutation.isPending}
        createPending={createMutation.isPending}
        disabled={!linesValid || !totalsMatch || !companyId}
        onCancel={() => router.history.back()}
      />
    </form>
  );
}

// ── Standalone form (from Commitments tab — no buy log line) ──

type CommitmentType = "subcontract" | "purchase_order" | "prime_contract";

const COMMITMENT_TYPE_LABELS: Record<CommitmentType, string> = {
  subcontract: "Subcontract",
  purchase_order: "Purchase Order",
  prime_contract: "Prime Contract",
};

function StandaloneCommitmentForm({
  projectId,
  costCodes,
}: {
  projectId: string;
  costCodes: CostCode[];
}) {
  const router = useRouter();
  const createMutation = useCreateCommitment();
  const uploadMutation = useUploadContractDocument();
  const companies = useCompanies();

  const [commitmentType, setCommitmentType] = useState<CommitmentType>("subcontract");
  const [status, setStatus] = useState<InitialStatus>("draft");
  const [title, setTitle] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [originalAmount, setOriginalAmount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [scheduledEndDate, setScheduledEndDate] = useState("");
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [lines, setLines] = useState<DraftG703Line[]>([freshLine(1, costCodes[0]?.id ?? "")]);

  const isPending = createMutation.isPending || uploadMutation.isPending;
  const linesValid =
    lines.length > 0 && lines.every((l) => l.description.trim() && l.itemNo.trim());

  const subcontractorCompanies = (companies.data ?? []).filter((c) => {
    if (!c.type) return true;
    const allowed = new Set([
      "subcontractor",
      "vendor",
      "contractor",
      "general_contractor",
      "material_supplier",
    ]);
    return allowed.has(c.type);
  });

  function updateLine(localId: string, patch: Partial<DraftG703Line>) {
    setLines((prev) => prev.map((l) => (l.localId === localId ? { ...l, ...patch } : l)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!linesValid || !title.trim()) return;

    const hasG703 = lines.some((l) => toNumber(l.scheduledValue) > 0);

    const payload: CreateCommitmentPayload = {
      projectId,
      companyId: companyId || null,
      toCompanyId: companyId || null,
      commitmentType,
      direction: commitmentType === "prime_contract" ? "inbound" : "outbound",
      status,
      title: title.trim(),
      originalAmount: originalAmount || null,
      startDate: startDate || undefined,
      scheduledEndDate: scheduledEndDate || undefined,
      lineItems: hasG703
        ? lines.map((l) => ({
            itemNo: l.itemNo,
            description: l.description,
            scheduledValue: l.scheduledValue === "" ? "0" : l.scheduledValue,
            costCodeId: l.costCodeId || null,
            isAllowance: false,
          }))
        : undefined,
    };

    createMutation.mutate(payload, {
      onSuccess: async (res) => {
        const newId = res.data.id;
        if (contractFile && newId) {
          try {
            await uploadMutation.mutateAsync({ id: newId, file: contractFile });
            toast.success("Contract document uploaded");
          } catch {
            return;
          }
        }
        router.history.back();
      },
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto space-y-5 px-6 py-5">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div className="col-span-2 space-y-1.5 md:col-span-3">
            <Label htmlFor="s-title">Title *</Label>
            <Input
              id="s-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Prime contract with owner"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-type">Commitment Type</Label>
            <Select
              value={commitmentType}
              onValueChange={(v) => setCommitmentType(v as CommitmentType)}
            >
              <SelectTrigger id="s-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(COMMITMENT_TYPE_LABELS) as CommitmentType[]).map((t) => (
                  <SelectItem key={t} value={t}>
                    {COMMITMENT_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-status">Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as InitialStatus)}>
              <SelectTrigger id="s-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(STATUS_LABELS) as InitialStatus[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-amount">Original Amount</Label>
            <Input
              id="s-amount"
              value={originalAmount}
              onChange={(e) => setOriginalAmount(e.target.value)}
              placeholder="0.00"
              type="number"
              min="0"
              step="0.01"
            />
          </div>
          <div className="col-span-2 space-y-1.5 md:col-span-3">
            <Label htmlFor="s-company">Company</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger id="s-company">
                <SelectValue placeholder="Select a company (optional)" />
              </SelectTrigger>
              <SelectContent>
                {subcontractorCompanies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.companyName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Start Date</Label>
            <DatePicker
              value={startDate || null}
              onChange={(next) => setStartDate(next ?? "")}
              ariaLabel="Start date"
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Scheduled End Date</Label>
            <DatePicker
              value={scheduledEndDate || null}
              onChange={(next) => setScheduledEndDate(next ?? "")}
              ariaLabel="Scheduled end date"
              className="w-full"
            />
          </div>
        </div>

        <FileUploadField
          file={contractFile}
          onSelect={setContractFile}
          onClear={() => setContractFile(null)}
        />

        {costCodes.length > 0 && (
          <G703Table
            lines={lines}
            costCodes={costCodes}
            defaultCostCodeId={costCodes[0]?.id ?? ""}
            totalAward={null}
            onUpdate={updateLine}
            onAdd={() =>
              setLines((prev) => [...prev, freshLine(prev.length + 1, costCodes[0]?.id ?? "")])
            }
            onRemove={(id) => setLines((prev) => prev.filter((l) => l.localId !== id))}
          />
        )}
      </div>

      <FormFooter
        isPending={isPending}
        uploadPending={uploadMutation.isPending}
        createPending={createMutation.isPending}
        disabled={!title.trim() || !linesValid}
        onCancel={() => router.history.back()}
      />
    </form>
  );
}

// ── Page ──

export function CommitmentNewPage() {
  const router = useRouter();
  const rawSearch = useSearch({ strict: false });
  const projectId = typeof rawSearch.projectId === "string" ? rawSearch.projectId : "";
  const budgetLineId = typeof rawSearch.budgetLineId === "string" ? rawSearch.budgetLineId : "";

  const isBuyLogFlow = !!budgetLineId;

  const budget = useProjectBudget(projectId);
  const costCodesQuery = useProjectCostCodes(projectId);

  const lineItem = isBuyLogFlow
    ? budget.data?.lineItems.find((l) => l.id === budgetLineId)
    : undefined;

  if (!projectId) {
    return (
      <div>
        <div className="sticky top-0 z-10 -mx-6 border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => router.history.back()}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-semibold text-gray-900">Create Commitment</h1>
          </div>
        </div>
        <div className="flex h-48 items-center justify-center text-sm text-gray-500">
          No project selected.
        </div>
      </div>
    );
  }

  if (budget.isLoading || costCodesQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isBuyLogFlow && !lineItem) {
    return (
      <div>
        <div className="sticky top-0 z-10 -mx-6 border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => router.history.back()}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-semibold text-gray-900">Create Commitment</h1>
          </div>
        </div>
        <div className="flex h-48 items-center justify-center text-sm text-gray-500">
          Buy Log line item not found.
        </div>
      </div>
    );
  }

  // -m-6 cancels main's p-6 padding so the page fills edge-to-edge.
  // h-[calc(100vh-97px)] = 100vh minus layout header (h-16=64px) and layout footer (~33px),
  // giving us exactly main's clientHeight for a proper internal-scroll flex column layout.
  return (
    <div className="-m-6 flex h-[calc(100vh-97px)] flex-col">
      <div className="shrink-0 border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => router.history.back()}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Create Commitment</h1>
            <p className="text-sm text-gray-500">
              {isBuyLogFlow
                ? "Formalize this award as a subcontract. The commitment number is assigned automatically."
                : "Create a new commitment for this project. The commitment number is assigned automatically."}
            </p>
          </div>
        </div>
      </div>

      {isBuyLogFlow && lineItem ? (
        <BuyLogCommitmentForm
          lineItem={lineItem}
          projectId={projectId}
          costCodes={costCodesQuery.data ?? []}
        />
      ) : (
        <StandaloneCommitmentForm projectId={projectId} costCodes={costCodesQuery.data ?? []} />
      )}
    </div>
  );
}
